import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import { UiSuspensefulOutlet } from '@experiences/ui-common';
import type { PropsWithChildren } from 'react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    AuditLogDialog,
    AuditLogs,
    TenantAudit,
} from '../../common/constants/RouteNames';
import { routePaths } from '../../common/constants/routePaths';
import { useOrganizationName } from '../../common/hooks/useOrganizationName';
import BreadcrumbProvider from '../../common/providers/BreadcrumbProvider';
import { useTenantIdContext } from '../../common/providers/TenantIdProvider';
import { AuditRouteAccessGuard } from '../../container/helpers/AuditRouteAccessGuard';
import type { UiRouteObject } from '../../container/routeConfigs/UiRouteObject';
import { useIsAuditorOnly } from './useIsAuditorOnly';

const AuditPageComponentLegacy = React.lazy(() => import('./AuditPageComponentLegacy'));
const AuditPage = React.lazy(() => import('./AuditPage'));
const AuditDialogComponentLegacy = React.lazy(() => import('./AuditDialogComponentLegacy'));

const AuditProvider: React.FC<PropsWithChildren<{ isTenant?: boolean }>> = ({
    children, isTenant,
}) => {
    const { formatMessage: translate } = useIntl();

    const organizationName = useOrganizationName();

    const { tenantName } = useTenantIdContext();

    const breadCrumbLinks = useMemo(() => isTenant ? [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminTenantHome,
            name: tenantName,
        },
        {
            index: 2,
            link: routePaths.adminTenantAudit,
            name: translate({ id: 'CLIENT_AUDIT_LOG' }),
        },
    ] : [
        {
            index: 0,
            link: routePaths.admin,
            name: organizationName,
        },
        {
            index: 1,
            link: routePaths.adminAudit,
            name: translate({ id: 'CLIENT_AUDIT_LOG' }),
        },
    ], [ isTenant, organizationName, tenantName, translate ]);

    return <BreadcrumbProvider breadcrumbs={breadCrumbLinks}>
        {children}
    </BreadcrumbProvider>;
};

const AuditPageSwitcherComponent: React.FC<PropsWithChildren> = () => {
    const EnableUnifiedAuditExperience = useFeatureFlagValue(Features.EnableUnifiedAuditExperience.name);
    const isAuditorOnly = useIsAuditorOnly();

    // Auditor-role users always get unified: v1 is organization-admin-only and would 403 for them.
    return EnableUnifiedAuditExperience || isAuditorOnly
        ? <AuditPage />
        : <AuditPageComponentLegacy />;
};

export const AuditRoutes: UiRouteObject = {
    path: routePaths.adminAudit,
    element: <AuditRouteAccessGuard>
        <AuditProvider>
            <UiSuspensefulOutlet />
        </AuditProvider>
    </AuditRouteAccessGuard>,
    children: [
        {
            path: routePaths.adminAudit,
            redirectUrls: [ AuditLogs ],
            element: <AuditPageSwitcherComponent />,
            children: [
                {
                    path: routePaths.adminAuditDialog,
                    redirectUrls: [ AuditLogDialog ],
                    element: <AuditDialogComponentLegacy />,
                },
            ],
        },
    ],
};

export const TenantAuditRoutes: UiRouteObject = {
    path: routePaths.adminTenantAudit,
    element: <AuditRouteAccessGuard>
        <AuditProvider isTenant>
            <UiSuspensefulOutlet />
        </AuditProvider>
    </AuditRouteAccessGuard>,
    explicitEnvironments: [ 'cloud', 'k8s' ],
    children: [
        {
            path: routePaths.adminTenantAudit,
            redirectUrls: [ TenantAudit ],
            element: <AuditPage />,
        },
    ],
};
